import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import { getSnapshot } from 'mobx-state-tree';

import { ActionButton } from 'core/components/action-button';
import { useStore } from 'core/store';
import { OfferStatus } from 'core/store/offer-store';
import useErrors from 'common/hooks/use-errors';
import x from 'core/assets/svg/times.svg';

import {
  purchaseFailed,
  branchSpinner,
  somethingWrong,
  systemError,
  vinDiscrepancy,
  mvrRetry,
  mvrRejected,
  duplicatedBind,
  matchingActivePolicyWarning
} from './resolvers';
import useStyles from './dialog.styles';

const getOnlyErrors = (code) => code < 10000;
const getOnlyRejections = (code) => code >= 10000;

// "System level" offer-flow errors get the unified, less-alarming modal from
// GitHub #30224: unknown error (code 0), power-outage errors (codes 1-4001),
// and the "something went wrong" error (code 4002). Higher codes are
// underwriting rejections/alerts and keep their existing modals.
const isOfferSystemError = (code) => Number(code) >= 0 && Number(code) <= 4002;

const OfferDialog = observer(({ onClose, fromCheckout }) => {
  const classes = useStyles();
  const { offer: store } = useStore();
  const errors = useErrors(getSnapshot(store.errors), getOnlyErrors);
  const rejections = useErrors(getSnapshot(store.errors), getOnlyRejections);
  const isSystemError = errors[0] && isOfferSystemError(errors[0].code);
  const vinDiscrepancies = store.vinDiscrepancies;

  // don't let the user close the dialog if we're in the middle of a purchase
  const hideCloseButton = fromCheckout && store.status === OfferStatus.Purchasing;

  const ModalContent = useMemo(() => {
    if (fromCheckout && store.status === OfferStatus.Purchasing) {
      return branchSpinner;
    }
    if (
      fromCheckout &&
      (store.status === OfferStatus.PurchaseFailed || store.status === OfferStatus.PreBindRejectionsBlocking)
    ) {
      return purchaseFailed;
    }
    if (vinDiscrepancies && vinDiscrepancies.length) {
      return vinDiscrepancy;
    }
    if (fromCheckout && store.status === OfferStatus.MVRRetry) {
      return mvrRetry;
    }
    if ((fromCheckout && store.status === OfferStatus.MVRRejected) || store.status === OfferStatus.MVRDriversChanged) {
      return mvrRejected;
    }

    if (fromCheckout && store.status === OfferStatus.DuplicatedBindError) {
      return duplicatedBind;
    }

    // #35249: matching active policy — confirmable "purchase anyway?" warning, not a failure.
    if (fromCheckout && store.status === OfferStatus.MatchingActivePolicyWarning) {
      return matchingActivePolicyWarning;
    }

    if (isSystemError) {
      return systemError;
    }
    return somethingWrong;
  }, [isSystemError, fromCheckout, store.status, vinDiscrepancies]);

  if (!ModalContent) {
    return null;
  }

  // Closing a system-error dialog must also drain store.errors. Otherwise the
  // leftover error would keep isSystemError truthy and could re-surface the
  // modal the next time the dialog is opened for any reason.
  const handleClose = () => {
    if (isSystemError) {
      store.clearErrors();
    }
    onClose();
  };

  return (
    <Dialog maxWidth="lg" open={!!store.openDialog}>
      <DialogContent className={classes.mContent}>
        {ModalContent && (
          <ModalContent classes={classes} errors={errors} rejections={rejections} vinDiscrepancies={vinDiscrepancies} />
        )}
        {!hideCloseButton && (
          <ActionButton size="big" type="edit" icon={x} className={classes.closeButton} onClick={handleClose} />
        )}
      </DialogContent>
    </Dialog>
  );
});

OfferDialog.defaultProps = {
  fromCheckout: false
};

OfferDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  fromCheckout: PropTypes.bool
};

export default OfferDialog;
